import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Loader2 } from "lucide-react";
import { useToast } from "../../Toast";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase"; // Make sure this import is correct for your project

const FormInfo = ({
  show,
  onClose,
  formTitle,
  setFormTitle,
  formDescription,
  setFormDescription,
  formImage,
  setFormImage,
  // handleFormImageUpload,
  category,
  setCategory,
  // onCategoryChange,
  saveFormDetails,
  formId,
}) => {
  const { addToast } = useToast();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Local buffered state so closing without saving reverts changes
  const [localFormTitle, setLocalFormTitle] = useState(formTitle || "");
  const [localFormDescription, setLocalFormDescription] = useState(
    formDescription || ""
  );
  const [localFormImage, setLocalFormImage] = useState(formImage || null);
  const [localCategory, setLocalCategory] = useState(category || "other");

  // Sync local buffer when dialog opens or source props change
  useEffect(() => {
    if (show) {
      setLocalFormTitle(formTitle || "");
      setLocalFormDescription(formDescription || "");
      setLocalFormImage(formImage || null);
      setLocalCategory(category || "other");
    }
  }, [show, formTitle, formDescription, formImage, category]);

  // Handle category change and notify parent
  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    console.log("FormInfo - Category before change:", localCategory);
    console.log("FormInfo - Changing category to:", newCategory);
    setLocalCategory(newCategory);
  };

  // Function to upload image to Firebase Storage
  const uploadImageToFirebase = useCallback(
    async (file) => {
      if (!file || !formId) return null;

      try {
        setIsUploading(true);
        setUploadProgress(0);

        // Create a reference to Firebase Storage
        const storageRef = ref(storage, `form-images/${formId}/${file.name}`);

        // Upload the file with progress tracking
        const uploadTask = uploadBytesResumable(storageRef, file);

        // Return a promise that resolves with the download URL
        return new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              // Track upload progress
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              // Handle errors
              setIsUploading(false);
              addToast(`Upload failed: ${error.message}`, "error");
              reject(error);
            },
            async () => {
              // On complete
              try {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref
                );
                setIsUploading(false);
                resolve(downloadURL);
              } catch (err) {
                setIsUploading(false);
                addToast(`Failed to get download URL: ${err.message}`, "error");
                reject(err);
              }
            }
          );
        });
      } catch (error) {
        setIsUploading(false);
        addToast(`Upload error: ${error.message}`, "error");
        return null;
      }
    },
    [formId, addToast]
  );

  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const handleSaveClick = async () => {
    try {
      setIsSavingDetails(true);
      // Commit local state to global for immediate UI update
      setFormTitle(localFormTitle);
      setFormDescription(localFormDescription);
      setFormImage(localFormImage);
      setCategory(localCategory);
      // Persist to backend with explicit fields
      await saveFormDetails({
        formTitle: localFormTitle,
        formDescription: localFormDescription,
        formImage: localFormImage,
        category: localCategory,
      });
      onClose();
    } catch (err) {
      // toast is handled inside saveFormDetails
    } finally {
      setIsSavingDetails(false);
    }
  };

  // Enhanced image upload handler
  const handleImageUpload = useCallback(
    async (file) => {
      if (!file) return;

      const maxSize = 4 * 1024 * 1024; // 4MB in bytes
      if (file.size > maxSize) {
        addToast("Image is too large (max 4MB)", "warning");
        return;
      }

      // Check file type
      if (!file.type.startsWith("image/")) {
        addToast("Only image files are allowed", "warning");
        return;
      }

      try {
        // First show a preview from the local file
        const reader = new FileReader();
        reader.onload = () => {
          console.log("FormInfo - Showing image preview");
          setLocalFormImage(reader.result);
        };
        reader.readAsDataURL(file);

        // Then upload to Firebase Storage
        const downloadURL = await uploadImageToFirebase(file);
        if (downloadURL) {
          console.log("FormInfo - Image uploaded to Firebase:", downloadURL);
          setLocalFormImage(downloadURL);
          addToast(
            "Image uploaded successfully - please save the form to persist the changes",
            "success"
          );
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        addToast("Failed to upload image", "error");
      }
    },
    [addToast, uploadImageToFirebase]
  );

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragging) setIsDragging(true);
    },
    [isDragging]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleImageUpload(file);
      }
    },
    [handleImageUpload]
  );

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center"
          style={{ isolation: "isolate" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => {
              if (isUploading || isSavingDetails) return;
              onClose();
            }}
          />

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0, transform: "translate(-50%, -50%)" }} // Include transform in animation
            exit={{ opacity: 0, y: -20 }}
            transition={{ type: "spring", damping: 25 }}
            className="absolute top-1/2 left-1/2 w-full max-w-2xl p-6 bg-white rounded-xl shadow-xl space-y-4 z-[100]"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">
                Form Details
              </h2>
              <button
                onClick={() => {
                  if (isUploading || isSavingDetails) return;
                  onClose();
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form Title
                </label>
                <input
                  type="text"
                  value={localFormTitle}
                  onChange={(e) => {
                    console.log(
                      "FormInfo - Changing formTitle to:",
                      e.target.value
                    );
                    setLocalFormTitle(e.target.value);
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter form title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form Description
                </label>
                <textarea
                  value={localFormDescription}
                  onChange={(e) => {
                    console.log(
                      "FormInfo - Changing formDescription to:",
                      e.target.value
                    );
                    setLocalFormDescription(e.target.value);
                  }}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter form description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form Category
                </label>
                <select
                  value={localCategory}
                  onChange={handleCategoryChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="other">Other</option>
                  <option value="general">General Knowledge</option>
                  <option value="educational">Educational</option>
                  <option value="training">Training</option>
                  <option value="certification">Certification</option>
                  <option value="skillbuilding">Skill-Building</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form Image
                </label>

                {localFormImage ? (
                  <div className="relative w-full h-40 mb-2">
                    <img
                      src={localFormImage}
                      alt="Form Header"
                      className="w-full h-full object-cover rounded-lg"
                    />
                    {isUploading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center rounded-lg">
                        <div className="flex flex-col items-center justify-center space-y-2">
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                          <p className="text-sm text-white font-medium">
                            Uploading... {Math.round(uploadProgress)}%
                          </p>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        console.log("FormInfo - Removing formImage");
                        setLocalFormImage(null);
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`w-full h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-gray-400 mb-2 transition-colors cursor-pointer ${
                      isDragging
                        ? "border-blue-500 bg-blue-50"
                        : "hover:border-blue-400 hover:bg-gray-50"
                    }`}
                  >
                    <Upload className="w-8 h-8 mb-2" />
                    <p className="text-sm font-medium">
                      Drag & drop an image here or click to browse
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PNG, JPG, GIF up to 4MB
                    </p>
                    {/* {isUploading ? (
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                        <p className="text-sm text-blue-500 font-medium">
                          Uploading... {Math.round(uploadProgress)}%
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mb-2" />
                        <p className="text-sm font-medium">
                          Drag & drop an image here or click to browse
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PNG, JPG, GIF up to 4MB
                        </p>
                      </>
                    )} */}
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  id="form-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                />

                {!localFormImage && !isUploading && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    disabled={isUploading}
                  >
                    Upload Image
                  </button>
                )}
              </div>
              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={onClose}
                  disabled={isUploading || isSavingDetails}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={handleSaveClick}
                  disabled={isUploading || isSavingDetails}
                >
                  {isSavingDetails ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FormInfo;
